import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

export const validatePhone = createAsyncThunk(
  "termLife/validatePhone",
  async ({ mobile, policyType }, { rejectWithValue }) => {
    try {
      console.log("\nValidate Phone Api Payload in Thunk: ", mobile);
      const payload = { msisdn: mobile, product: policyType };
      const res = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/auth/checkexistinguser`,
        JSON.stringify(payload),
        { headers: { "Content-Type": "application/json" } }
      );
      console.log("Validate Phone Api Response: ", res.data);
      return res.data;
    } catch (err) {
      console.error("CheckExistingUser Thunk error details:", {
        status: err.response?.status,
        data: err.response?.data,
      });
      return rejectWithValue(err.response?.data || "Something went wrong");
    }
  }
);

const initialState = {
  // From 1st TermLife Form => Stepper SignUpForm 
  formData: {
    gender: "",
    dob: "1995-01-01",
    mobile: "",
    policyTerm: "",
    coverAmount: "",
    cnic: "",
    issuanceDate: "",
    expiryDate: "",
    city: "",
    paymentMethod: "jazzcash",
    paymentFrequency: "Yearly",
  },
  beneficiaryForm: {
    name: "",
    relation: "",
    dob: "",
    age: "",
    mobile: "",
    cnic: "",
    guardianName: "",
    guardianRelation: "",
    guardianMobile: "",
    guardianCnic: "",
    beneficiary_cnic_front_image: null,
    beneficiary_cnic_back_image: null,
  },
  addOns: [],
  premiumResult: {},
  beneficiaries: [],
  beneficiaryAllocations: [],
  loading: false,
  error: null,
  phoneStatus: null,
  userData: null,
  token: null,
  draftId: null,
};

const termLifeSlice = createSlice({
  name: "termLife",
  initialState,
  reducers: {
    saveFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    // signup API response data
    saveUserData: (state, action) => {
      console.log("🔄 TermLife Reducer saveUserData is called with :", action.payload);

      state.userData = action.payload?.data || action.payload;
      state.token = action.payload?.accessToken || null;

      // Merge useful SignUp Response user fields into formData if present (e.g., gender, dob, mobile)
      const returnedData = action.payload?.data || action.payload || {};
      const fieldsToMerge = {};
      if (returnedData.gender) fieldsToMerge.gender = returnedData.gender;
      if (returnedData.dob) fieldsToMerge.dob = returnedData.dob;
      if (returnedData.mobile) fieldsToMerge.mobile = returnedData.mobile;
      if (returnedData.email) fieldsToMerge.email = returnedData.email;
      if (returnedData.cnic) fieldsToMerge.cnic = returnedData.cnic;
      if (Object.keys(fieldsToMerge).length) {
        state.formData = { ...state.formData, ...fieldsToMerge };
        console.log("✅ TermLife: Merged fields into formData:", fieldsToMerge);
      }

      console.log("✅ TermLife: userData and formData updated:", {
        userData: state.userData,
        formData: state.formData
      });
    },
    clearUserData: (state) => {
      state.userData = null;
      state.token = null;
    },
    saveAddOns: (state, action) => {
      state.addOns = action.payload;
      state.premiumResult = { ...state.premiumResult, addOns: action.payload };
    },
    savePremium: (state, action) => {
      state.premiumResult = action.payload;
    },
    setPersonalInfos: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    addBeneficiary: (state, action) => {
      state.beneficiaries.push(action.payload);
    },
    deleteBeneficiary: (state, action) => {
      const indexToDelete = action.payload;
      const beneficiaryToDelete = state.beneficiaries[indexToDelete];

      if (beneficiaryToDelete) {
        // Remove beneficiary by index
        state.beneficiaries = state.beneficiaries.filter((_, index) => index !== indexToDelete);

        // Also clean up the beneficiary allocations for the deleted beneficiary
        if (state.beneficiaryAllocations && state.beneficiaryAllocations.length > 0) {
          // Remove allocation by matching name and relation
          const identifier = `${beneficiaryToDelete.name}-${beneficiaryToDelete.relation}`;
          state.beneficiaryAllocations = state.beneficiaryAllocations.filter(
            allocation => allocation.id !== identifier
          );
        }
      }
    },
    setBeneficiaryForm: (state, action) => {
      state.beneficiaryForm = { ...state.beneficiaryForm, ...action.payload };
    },
    saveBeneficiaryAllocations: (state, action) => {
      state.beneficiaryAllocations = action.payload;
    },
    resetBeneficiaries: (state) => {
      state.beneficiaries = [];
    },
    // Save draft ID when policy is saved for later
    setDraftId: (state, action) => {
      state.draftId = action.payload;
    },
    // Clear draft ID
    clearDraftId: (state) => {
      state.draftId = null;
    },
    // Reset all termLife data when user logs out
    resetTermLifeData: (state) => {
      // Return a deep copy of the initial state to ensure all fields (including dynamically added ones) are properly reset
      return JSON.parse(JSON.stringify(initialState));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validatePhone.pending, (state) => {
        state.loading = true;
        state.error = null;
        toast.info("Validating phone...");
      })
      .addCase(validatePhone.fulfilled, (state, action) => {
        state.loading = false;
        state.phoneStatus = action.payload?.status || null;
        toast.success(
          action.payload?.message || "Phone validated successfully"
        );
      })
      .addCase(validatePhone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(
          typeof action.payload === "string"
            ? action.payload
            : action.payload?.message || "Failed to validate phone"
        );
      });
  },
});

export const {
  saveFormData,
  saveUserData,
  clearUserData,
  saveAddOns,
  savePremium,
  setPersonalInfos,
  addBeneficiary,
  deleteBeneficiary,
  resetBeneficiaries,
  setBeneficiaryForm,
  saveBeneficiaryAllocations,
  setDraftId,
  clearDraftId,
  resetTermLifeData,
} = termLifeSlice.actions;
export default termLifeSlice.reducer;
